import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import PaidIcon from "@mui/icons-material/Paid";
import { AccountingService } from "services/accounting";
import { MemberPicker } from "components/MemberPicker";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const fmtCLP = (n) => (n == null || isNaN(n) ? "—" : "$" + Math.round(n).toLocaleString("es-CL"));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("es-CL") : "—");

const STATUS_COLORS = {
  PENDING: "default",
  DECLARED: "info",
  PAID: "success",
  OVERDUE: "error",
  WAIVED: "warning",
};

function DeclareDialog({ open, onClose, obligation, onSubmit }) {
  const [folio, setFolio] = useState("");
  const [declaredAmount, setDeclaredAmount] = useState("");

  useEffect(() => {
    if (obligation) {
      setFolio("");
      setDeclaredAmount(obligation.estimatedAmount?.toString() || "");
    }
  }, [obligation]);

  if (!obligation) return null;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Declarar {obligation.type}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        <TextField label="Folio (opcional)" value={folio} onChange={(e) => setFolio(e.target.value)} size="small" />
        <TextField label="Monto declarado (CLP)" type="number" value={declaredAmount} onChange={(e) => setDeclaredAmount(e.target.value)} size="small" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => onSubmit({ folio: folio || undefined, declaredAmount: declaredAmount ? Number(declaredAmount) : undefined })}>
          Marcar declarada
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TributarioView() {
  const [memberId, setMemberId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [recalcOpen, setRecalcOpen] = useState(false);
  const now = new Date();
  const [recalcPeriod, setRecalcPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [declareTarget, setDeclareTarget] = useState(null);

  const load = async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await AccountingService.listTaxObligations(memberId);
      setItems(res.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  const handleRecalc = async () => {
    try {
      const res = await AccountingService.recalculateTaxObligations(memberId, { period: recalcPeriod });
      setFeedback({ kind: "ok", msg: `Recalculado: ${res.created?.length || 0} obligaciones, ${res.overdueMarked || 0} marcadas overdue` });
      setRecalcOpen(false);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handleDeclare = async (payload) => {
    try {
      await AccountingService.declareTaxObligation(memberId, declareTarget._id, payload);
      setFeedback({ kind: "ok", msg: "Marcada como declarada" });
      setDeclareTarget(null);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handlePaid = async (id) => {
    try {
      await AccountingService.markTaxObligationPaid(memberId, id);
      setFeedback({ kind: "ok", msg: "Marcada como pagada" });
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  // Agrupar por estado para vista resumida
  const pending = items.filter((i) => i.status === "PENDING");
  const overdue = items.filter((i) => i.status === "OVERDUE");
  const declared = items.filter((i) => i.status === "DECLARED");
  const paid = items.filter((i) => i.status === "PAID");
  const totalEstimadoPendiente = [...pending, ...overdue].reduce((s, o) => s + (o.estimatedAmount || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Calendario Tributario</Typography>
        <MemberPicker value={memberId} onChange={setMemberId} />
      </Box>

      {feedback && <Alert severity={feedback.kind === "ok" ? "success" : "error"} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!memberId ? (
        <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>Selecciona un cliente</Paper>
      ) : (
        <>
          {/* KPIs resumidos */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Pendientes</Typography>
              <Typography variant="h4" color="warning.main">{pending.length + overdue.length}</Typography>
              <Typography variant="caption">{fmtCLP(totalEstimadoPendiente)}</Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Vencidas</Typography>
              <Typography variant="h4" color="error.main">{overdue.length}</Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Declaradas</Typography>
              <Typography variant="h4" color="info.main">{declared.length}</Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Pagadas</Typography>
              <Typography variant="h4" color="success.main">{paid.length}</Typography>
            </Paper>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => setRecalcOpen(true)}>
              Recalcular obligaciones
            </Button>
          </Box>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Período</TableCell>
                    <TableCell>Vencimiento</TableCell>
                    <TableCell align="right">Estimado</TableCell>
                    <TableCell align="right">Declarado</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Folio</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                  ) : items.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No hay obligaciones registradas. Pulsa "Recalcular" para generar las del período actual.
                    </TableCell></TableRow>
                  ) : items.map((o) => (
                    <TableRow key={o._id} hover>
                      <TableCell><Chip size="small" label={o.type} /></TableCell>
                      <TableCell>{o.period.month ? `${String(o.period.month).padStart(2, "0")}/${o.period.year}` : o.period.year}</TableCell>
                      <TableCell>{fmtDate(o.dueDate)}</TableCell>
                      <TableCell align="right">{fmtCLP(o.estimatedAmount)}</TableCell>
                      <TableCell align="right">{fmtCLP(o.declaredAmount)}</TableCell>
                      <TableCell><Chip size="small" label={o.status} color={STATUS_COLORS[o.status] || "default"} /></TableCell>
                      <TableCell>{o.folio || "—"}</TableCell>
                      <TableCell align="right">
                        {(o.status === "PENDING" || o.status === "OVERDUE") && (
                          <Button size="small" startIcon={<CheckIcon />} onClick={() => setDeclareTarget(o)}>Declarar</Button>
                        )}
                        {o.status === "DECLARED" && (
                          <Button size="small" color="success" startIcon={<PaidIcon />} onClick={() => handlePaid(o._id)}>Pagada</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Dialog recalc */}
          <Dialog open={recalcOpen} onClose={() => setRecalcOpen(false)}>
            <DialogTitle>Recalcular obligaciones</DialogTitle>
            <DialogContent sx={{ display: "flex", gap: 2, pt: 2 }}>
              <Select size="small" value={recalcPeriod.month} onChange={(e) => setRecalcPeriod({ ...recalcPeriod, month: e.target.value })}>
                {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
              </Select>
              <Select size="small" value={recalcPeriod.year} onChange={(e) => setRecalcPeriod({ ...recalcPeriod, year: e.target.value })}>
                {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRecalcOpen(false)}>Cancelar</Button>
              <Button variant="contained" onClick={handleRecalc}>Recalcular</Button>
            </DialogActions>
          </Dialog>

          <DeclareDialog
            open={!!declareTarget}
            obligation={declareTarget}
            onClose={() => setDeclareTarget(null)}
            onSubmit={handleDeclare}
          />
        </>
      )}
    </Box>
  );
}
